﻿import { ns } from "@/plugins/i18nFormat"

type SizeStrategy = 'utf8' | 'utf16'

export interface KeySizeRow {
	key: string
	keyBytes: number
	valueBytes: number
	totalBytes: number
}

export interface GroupRow {
	baseKey: string
	bytes: number
	keys: string[]
}

export interface AnalyzeOptions {
	sortDesc?: boolean;      	// default: true
	strategy?: SizeStrategy 	// default: 'utf8'
	groupByBaseKey?: boolean	// default: false
}

export interface AnalyzeResult {
	items: KeySizeRow[]
	total: { bytes: number; human: string }
	groups: GroupRow[]
}

export function byteLen ( str: string, strategy: SizeStrategy = 'utf8' ): number {
	if ( strategy === 'utf8' )
		return new TextEncoder().encode( str ).length

	// UTF-16 (2 bytes per unit)
	return str.length * 2
}

export function formatBytes ( bytes: number ): string {
	if ( bytes < 1024 )
		return ns( bytes ) + ' B'

	const kb = bytes / 1024

	if ( kb < 1024 )
		return ns( kb, kb < 10 ? 'bytes2' : 'bytes1' ) + ' kB'

	const mb = kb / 1024

	return ns( mb, mb < 10 ? 'bytes2' : 'bytes1' ) + ' MB'
}

// baseKey: everything before first square bracket at the end
function baseKeyOf ( key: string ): string {
	const m = key.match( /^(.*?)(\[[^\]]*\])?$/ )

	return m ? ( m[ 1 ] || key ) : key
}

export function analyzeLocalStorage ( opts: AnalyzeOptions = {} ): AnalyzeResult {
	const strategy: SizeStrategy = opts.strategy ?? 'utf8'

	const items: KeySizeRow[] = []
	const keys: string[] = []

	for ( let i = 0; i < localStorage.length; i++ ) {
		const k = localStorage.key( i )

		if ( k )
			keys.push( k )
	}

	for ( const key of keys ) {
		const value = localStorage.getItem( key ) ?? ''

		const keyBytes = byteLen( key, strategy )
		const valueBytes = byteLen( value, strategy )

		const totalBytes = keyBytes + valueBytes

		items.push( { key, keyBytes, valueBytes, totalBytes } )
	}

	items.sort( ( a, b ) => b.totalBytes - a.totalBytes )

	const totalBytes = items.reduce( ( s, r ) => s + r.totalBytes, 0 )

	const groupsMap = new Map<string, { bytes: number; keys: string[] }>()

	for ( const row of items ) {
		const base = baseKeyOf( row.key )
		const entry = groupsMap.get( base ) ?? { bytes: 0, keys: [] }

		entry.bytes += row.totalBytes
		entry.keys.push( row.key )

		groupsMap.set( base, entry )
	}

	const groups: GroupRow[] = Array
		.from( groupsMap.entries() )
		.map( ( [ baseKey, { bytes, keys } ] ) => ( { baseKey, bytes, keys } ) )
		.sort( ( a, b ) => b.bytes - a.bytes )

	return {
		items,
		groups,
		total: { bytes: totalBytes, human: formatBytes( totalBytes ) },
	}
}
